import { useEffect, useState } from 'react';

/**
 * Active-project tracker for the portfolio page's date sidebar.
 *
 * Originally this used GSAP ScrollTrigger, but ScrollTrigger's internal
 * refresh loop (re-evaluating triggers after every layout shift) was
 * thrashing with React's render cycle to the point where CSS transitions
 * on the project cards couldn't make progress. A plain scroll listener
 * is more than enough — it's just "which project's center is nearest
 * the viewport center?"
 *
 * The per-card entrance animations are now owned by each ProjectCard
 * (see PortfolioPage.tsx), which sets up its own IntersectionObserver
 * and toggles an `is-loaded` class via React state.
 */
export function usePortfolioScroll(
  containerRef: React.RefObject<HTMLElement>,
  projectRefs: React.MutableRefObject<(HTMLElement | null)[]>,
  deps: unknown[],
): { activeIndex: number; scrollToIndex: (i: number) => void } {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const elements = projectRefs.current.filter((el): el is HTMLElement => !!el);
    if (elements.length === 0) return;

    // Throttled to one update per scroll-tick to keep re-renders cheap.
    // We only call setActiveIndex when the active project actually changes,
    // so the rest of the React tree stays still during normal scrolling.
    let rafScheduled = false;
    let lastIndex = -1;
    const compute = () => {
      const center = window.scrollY + window.innerHeight / 2;
      let bestIndex = 0;
      let bestDist = Infinity;
      for (let i = 0; i < elements.length; i++) {
        const rect = elements[i].getBoundingClientRect();
        const midY = rect.top + window.scrollY + rect.height / 2;
        const dist = Math.abs(midY - center);
        if (dist < bestDist) {
          bestDist = dist;
          bestIndex = i;
        }
      }
      if (bestIndex !== lastIndex) {
        lastIndex = bestIndex;
        setActiveIndex(bestIndex);
      }
    };
    const onScroll = () => {
      if (rafScheduled) return;
      rafScheduled = true;
      requestAnimationFrame(() => {
        rafScheduled = false;
        compute();
      });
    };

    // Wait a beat before the initial compute. If we do it synchronously here
    // we end up calling setActiveIndex during the very first paint, and that
    // re-render lands right when project cards are starting their CSS
    // entrance animation — which the React commit will then interrupt.
    const initialTimer = window.setTimeout(compute, 800);

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      window.clearTimeout(initialTimer);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  function scrollToIndex(i: number) {
    const el = projectRefs.current[i];
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - 120;
    window.scrollTo({ top: y, behavior: 'smooth' });
  }

  void containerRef;
  return { activeIndex, scrollToIndex };
}
